import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL_AUTH } from '../config/api';

const AuthContext = createContext(null);

const TOKEN_REFRESH_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const TOKEN_REFRESH_TIMESTAMP_KEY = 'authTokenRefreshedAt';

async function tryRefreshToken(currentToken) {
  try {
    const response = await fetch(`${API_BASE_URL_AUTH}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      const data = await response.json();
      if (data.status === 1 && data.token) {
        return data.token;
      }
    }
  } catch (e) {
    console.warn('Token refresh failed (network):', e.message);
  }
  return null;
}

function shouldRefreshToken() {
  const lastRefresh = localStorage.getItem(TOKEN_REFRESH_TIMESTAMP_KEY);
  if (!lastRefresh) return true;
  return Date.now() - parseInt(lastRefresh, 10) > TOKEN_REFRESH_INTERVAL_MS;
}

function markTokenRefreshed() {
  localStorage.setItem(TOKEN_REFRESH_TIMESTAMP_KEY, Date.now().toString());
}

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [user, setUser] = useState(null);
  const [points, setPoints] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem(TOKEN_REFRESH_TIMESTAMP_KEY);
    setAuthToken(null);
    setUser(null);
    setPoints(null);
    setHasUploaded(false);
    setLoading(false);
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const updateToken = useCallback((newToken) => {
    localStorage.setItem('authToken', newToken);
    setAuthToken(newToken);
    markTokenRefreshed();
  }, []);

  const fetchUserDetailsAndPoints = useCallback(async (token) => {
    if (!token) {
      setUser(null);
      setPoints(null);
      setHasUploaded(false);
      setLoading(false);
      return;
    }
    try {
      const userResponse = await fetch(`${API_BASE_URL_AUTH}/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (userResponse.status === 401) {
        const newToken = await tryRefreshToken(token);
        if (newToken) {
          updateToken(newToken);
          setLoading(false);
          return;
        }
        console.warn('Token expired and refresh failed. Logging out.');
        logout();
        return;
      }

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser({
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          country_slug: userData.country_slug || null,
        });
      } else {
        console.error('Failed to fetch user details, status:', userResponse.status);
        setLoading(false);
        return;
      }

      const pointsResponse = await fetch(`${API_BASE_URL_AUTH}/user/points`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (pointsResponse.ok) {
        const pointsData = await pointsResponse.json();
        setPoints(pointsData.points);
        setHasUploaded(pointsData.has_uploaded || false);
      } else {
        console.error('Failed to fetch points, status:', pointsResponse.status);
        setPoints(0);
        setHasUploaded(false);
      }
    } catch (error) {
      console.error('Network error fetching user details:', error);
      // Keep the user logged in on network errors -- don't call logout()
    }
    setLoading(false);
  }, [logout, updateToken]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);

      if (shouldRefreshToken()) {
        tryRefreshToken(token).then((newToken) => {
          if (newToken) {
            updateToken(newToken);
            fetchUserDetailsAndPoints(newToken);
          } else {
            fetchUserDetailsAndPoints(token);
          }
        });
      } else {
        fetchUserDetailsAndPoints(token);
      }

      refreshTimerRef.current = setInterval(() => {
        const currentToken = localStorage.getItem('authToken');
        if (currentToken && shouldRefreshToken()) {
          tryRefreshToken(currentToken).then((newToken) => {
            if (newToken) updateToken(newToken);
          });
        }
      }, 24 * 60 * 60 * 1000); // check daily
    } else {
      setLoading(false);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []); // Run once on mount

  const login = useCallback((token, userDataFromLogin) => {
    updateToken(token);
    fetchUserDetailsAndPoints(token);
  }, [fetchUserDetailsAndPoints, updateToken]);

  const refreshPoints = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL_AUTH}/user/points`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (resp.ok) {
        const data = await resp.json();
        setPoints(data.points);
        setHasUploaded(data.has_uploaded || false);
      }
    } catch (e) {
      console.error('Failed to refresh points:', e);
    }
  }, []);

  const contextValue = useMemo(() => ({
    authToken, user, points, hasUploaded, login, logout, refreshPoints,
    isAuthenticated: !!authToken, authLoading: loading,
    fetchUserDetailsAndPoints,
  }), [authToken, user, points, hasUploaded, login, logout, refreshPoints, loading, fetchUserDetailsAndPoints]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 